import { LogoSymbol } from "@/components/Brand";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { track } from "@/lib/analytics";
import {
  ArrowLeft, ArrowRight, Loader2, Sparkles, Check, Wand2, X, Plus, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const PROPERTY_TYPES = ["Appartamento", "Casa vacanze", "B&B", "Villa", "Camera", "Residence", "Altro"];
const ACCESS = ["Consegna chiavi", "Keybox", "Smart lock", "Reception", "Self check-in", "Altro"];
const CHECKOUT_TASKS = ["Lasciare le chiavi", "Spegnere le luci", "Chiudere le finestre", "Gettare i rifiuti", "Lavare i piatti", "Chiudere l'acqua"];
const PARKING = ["Privato", "In strada", "Garage", "Parcheggio pubblico", "Non disponibile", "Altro"];
const RULE_CHIPS = [
  { v: "no_smoking", l: "Vietato fumare" }, { v: "no_parties", l: "No feste" },
  { v: "pets", l: "Animali ammessi" }, { v: "quiet_hours", l: "Orari di silenzio" },
];
const AMENITIES = [
  "Wi-Fi", "Aria condizionata", "Riscaldamento", "Smart TV", "Lavatrice", "Asciugatrice",
  "Lavastoviglie", "Macchina del caffè", "Cucina", "Ascensore", "Piscina", "Palestra",
  "Culla", "Letto supplementare", "Divano letto", "Deposito bagagli",
];

// Declarative step definitions. Each step reads/writes into a flat answers object.
const STEPS = [
  { id: "name", q: "Come si chiama la tua struttura?", type: "text", key: "name", placeholder: "Es. Casa Aurora", required: true },
  {
    id: "location", q: "Dove si trova?", type: "group", fields: [
      { key: "address", label: "Indirizzo", placeholder: "Via Roma 10" },
      { key: "city", label: "Città", placeholder: "Firenze" },
      { key: "postal_code", label: "CAP", placeholder: "50100" },
      { key: "country", label: "Paese", placeholder: "Italia" },
    ],
  },
  { id: "type", q: "Che tipo di struttura è?", type: "single", key: "property_type", options: PROPERTY_TYPES },
  { id: "checkin_time", q: "A che ora possono fare il check-in i tuoi ospiti?", type: "text", inputType: "time", key: "checkin_time", hint: "Puoi indicare un orario o un intervallo (es. dalle 15:00)." },
  {
    id: "access", q: "Come accedono alla struttura?", type: "single", key: "access_method", options: ACCESS,
    conditional: (a) => ["Keybox", "Smart lock", "Self check-in"].includes(a.access_method),
    condFields: [
      { key: "keybox_location", label: "Dove si trova la keybox / il dispositivo?", placeholder: "Accanto alla porta d'ingresso" },
      { key: "access_codes", label: "Come ricevono il codice?", placeholder: "Ti invio il codice il giorno dell'arrivo" },
    ],
  },
  { id: "checkout_time", q: "A che ora devono fare il check-out?", type: "text", inputType: "time", key: "checkout_time" },
  { id: "checkout_tasks", q: "Cosa devono fare prima di partire?", subtitle: "Seleziona tutto ciò che serve. Potrai aggiungere altro.", type: "multi", key: "checkout_tasks", options: CHECKOUT_TASKS, custom: "checkout_custom", customLabel: "Aggiungi un'istruzione" },
  {
    id: "wifi", q: "Hai una rete Wi-Fi per gli ospiti?", type: "yesno", key: "wifi_available",
    yesFields: [
      { key: "wifi_network", label: "Nome rete", placeholder: "CasaAurora_WiFi" },
      { key: "wifi_password", label: "Password", placeholder: "••••••••" },
      { key: "wifi_notes", label: "Istruzioni (facoltativo)", placeholder: "Il router è in salotto" },
    ],
  },
  {
    id: "parking", q: "È disponibile un parcheggio?", type: "single", key: "parking_type", options: PARKING,
    conditional: (a) => a.parking_type && a.parking_type !== "Non disponibile",
    condFields: [
      { key: "parking_price", label: "Prezzo (facoltativo)", placeholder: "Gratuito / €15 al giorno" },
      { key: "parking_instructions", label: "Istruzioni (facoltativo)", placeholder: "Posto n. 12 nel garage" },
    ],
  },
  { id: "rules", q: "Ci sono regole importanti da conoscere?", subtitle: "Seleziona quelle valide e aggiungi le tue.", type: "multi", key: "rules", options: RULE_CHIPS.map((r) => r.v), labels: RULE_CHIPS, custom: "rules_custom", customLabel: "Aggiungi una regola" },
  { id: "amenities", q: "Quali servizi sono disponibili?", subtitle: "Aiuta l'AI a preparare le istruzioni giuste.", type: "multi", key: "amenities", options: AMENITIES, custom: "amenities_custom", customLabel: "Aggiungi un servizio" },
  { id: "mobility", q: "Vuoi aiutare gli ospiti a muoversi?", subtitle: "Indica fermate, stazioni o aeroporti vicini. Non inventeremo linee o orari.", type: "textarea", key: "getting_around", placeholder: "Fermata bus a 200m. Stazione centrale a 10 min a piedi.", optional: true },
  {
    id: "contact", q: "Come possono contattarti gli ospiti?", type: "group", fields: [
      { key: "host_name", label: "Il tuo nome", placeholder: "Marco" },
      { key: "phone", label: "Telefono", placeholder: "+39 333 1234567", inputType: "tel" },
      { key: "whatsapp", label: "WhatsApp (facoltativo)", placeholder: "+39 333 1234567", inputType: "tel" },
      { key: "email", label: "Email (facoltativo)", placeholder: "host@email.it", inputType: "email" },
    ],
  },
  { id: "emergency", q: "Informazioni di emergenza (facoltativo)", subtitle: "Numeri utili per i tuoi ospiti.", type: "textarea", key: "emergency_info", placeholder: "Emergenze: 112. Ospedale più vicino a 2km.", optional: true },
];

function deriveAnswers(p) {
  const b = p.basic || {}, w = p.wifi || {}, c = p.contact || {}, ci = p.checkin || {}, co = p.checkout || {}, pk = p.parking || {}, ac = p.access || {};
  const a = {};
  if (p.name && !["Nuova struttura", "Struttura importata", "Untitled Property"].includes(p.name)) a.name = p.name;
  ["address", "city", "postal_code", "country", "property_type"].forEach((k) => { if (b[k]) a[k] = b[k]; });
  if (ci.time) a.checkin_time = ci.time;
  if (co.time) a.checkout_time = co.time;
  if (w.network) a.wifi_network = w.network;
  if (w.password) a.wifi_password = w.password;
  if (w.network || w.password) a.wifi_available = true;
  if (pk.availability) a.parking_type = pk.availability;
  if (pk.cost) a.parking_price = pk.cost;
  if (ac.keybox) a.keybox_location = ac.keybox;
  if (c.host_name) a.host_name = c.host_name;
  if (c.phone) a.phone = c.phone;
  if (c.email) a.email = c.email;
  if (c.whatsapp) a.whatsapp = c.whatsapp;
  if (Array.isArray(p.amenities) && p.amenities.length) a.amenities = p.amenities;
  if (p.getting_around) a.getting_around = p.getting_around;
  return a;
}

const Chip = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`px-4 py-2.5 rounded-2xl text-sm font-semibold border transition-all active:scale-95 ${active ? "bg-blue-600 text-white border-blue-600 shadow-sm shadow-blue-200" : "bg-white text-slate-600 border-slate-200"}`}>
    {children}
  </button>
);

const Field = ({ f, value, onChange }) => (
  <div>
    <label className="text-sm font-semibold text-slate-700">{f.label}</label>
    <input type={f.inputType || "text"} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
      data-testid={`wizard-field-${f.key}`} placeholder={f.placeholder}
      className="mt-1.5 w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-blue-500 text-[15px]" />
  </div>
);

export default function WizardSetup() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [prop, setProp] = useState(null);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [customInput, setCustomInput] = useState("");
  const [phase, setPhase] = useState("wizard"); // wizard | generating | done
  const [result, setResult] = useState(null);
  const saveTimer = useRef(null);

  useEffect(() => {
    api.get(`/properties/${id}`).then(({ data }) => {
      const p = data.property;
      setProp(p);
      const w = p.wizard || {};
      const seeded = (w.answers && Object.keys(w.answers).length) ? w.answers : deriveAnswers(p);
      setAnswers(seeded);
      setStep(Math.min(w.step || 0, STEPS.length - 1));
      setLoading(false);
      track("ai_wizard_started", id);
    }).catch(() => { toast.error("Struttura non trovata"); navigate("/dashboard/properties"); });
  }, [id, navigate]);

  const persist = useCallback((nextAnswers, nextStep) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.post(`/properties/${id}/wizard/save`, { step: nextStep, answers: nextAnswers }).catch(() => {});
    }, 400);
  }, [id]);

  const set = (key, value) => setAnswers((a) => { const n = { ...a, [key]: value }; persist(n, step); return n; });
  const toggle = (key, value) => setAnswers((a) => {
    const arr = Array.isArray(a[key]) ? a[key] : [];
    const n = { ...a, [key]: arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value] };
    persist(n, step); return n;
  });
  const addCustom = (key) => {
    const v = customInput.trim(); if (!v) return;
    setAnswers((a) => { const arr = Array.isArray(a[key]) ? a[key] : []; const n = { ...a, [key]: [...arr, v] }; persist(n, step); return n; });
    setCustomInput("");
  };

  const s = STEPS[step];
  const progress = Math.round(((step + 1) / (STEPS.length + 1)) * 100);
  const canNext = useMemo(() => {
    if (!s) return true;
    if (s.required) return !!(answers[s.key] || "").trim?.();
    return true;
  }, [s, answers]);

  const goNext = () => {
    const ns = step + 1;
    if (ns >= STEPS.length) { generate(); return; }
    setStep(ns); persist(answers, ns); setCustomInput("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goBack = () => { if (step === 0) { navigate("/dashboard/properties"); return; } const ns = step - 1; setStep(ns); persist(answers, ns); };

  const generate = async () => {
    setPhase("generating");
    try {
      const { data } = await api.post(`/properties/${id}/wizard/generate`, { step: STEPS.length, answers });
      setResult(data);
      setPhase("done");
      track("guide_generated", id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setPhase("wizard");
    }
  };

  const exitToManual = () => { toast.info("Puoi completare tutto manualmente"); navigate(`/dashboard/properties/${id}/edit`); };

  if (loading) return <div className="min-h-screen grid place-items-center bg-slate-50"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>;

  if (phase === "generating") return (
    <div className="min-h-screen bg-slate-50 grid place-items-center px-8 text-center">
      <div className="animate-fade-up">
        <LogoSymbol className="h-8 mx-auto mb-6 opacity-90" />
        <div className="w-16 h-16 rounded-3xl bg-blue-600 text-white grid place-items-center mx-auto animate-pulse"><Sparkles className="w-8 h-8" /></div>
        <h1 className="font-display font-extrabold text-2xl text-slate-900 mt-6">Perfetto. Preparo la tua guida.</h1>
        <p className="text-slate-500 mt-2">Sto organizzando le informazioni e creando contenuti chiari per i tuoi ospiti.</p>
        <Loader2 className="w-6 h-6 animate-spin text-blue-500 mx-auto mt-6" />
      </div>
    </div>
  );

  if (phase === "done" && result) {
    const percent = result.health?.percent ?? 0;
    const ok = (result.health?.checks || []).filter((c) => c.status === "ok").slice(0, 4);
    const missing = (result.health?.checks || []).filter((c) => c.status === "missing").slice(0, 4);
    return (
      <div className="min-h-screen bg-slate-50 max-w-md mx-auto px-5 py-10">
        <div className="animate-fade-up text-center">
          <div className="w-16 h-16 rounded-3xl bg-blue-600 text-white grid place-items-center mx-auto"><Check className="w-8 h-8" /></div>
          <h1 className="font-display font-extrabold text-2xl text-slate-900 mt-5" data-testid="wizard-done-title">La tua guida è pronta</h1>
          <p className="text-slate-500 mt-2">Abbiamo creato la prima versione della tua Guest Guide. Ora puoi controllarla, personalizzarla e pubblicarla.</p>
        </div>
        <div className="mt-6 rounded-3xl bg-white border border-slate-100 p-5">
          <p className="text-sm text-slate-500">La tua guida è completa al <span className="font-bold text-blue-600">{percent}%</span></p>
          <div className="mt-2 h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-blue-500 transition-all duration-700" style={{ width: `${percent}%` }} /></div>
          {ok.length > 0 && <div className="mt-4 flex flex-wrap gap-2">{ok.map((c) => <span key={c.key} className="inline-flex items-center gap-1 text-xs font-semibold bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full"><Check className="w-3 h-3" />{c.label}</span>)}</div>}
          {missing.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Da completare</p>
              <div className="mt-2 flex flex-wrap gap-2">{missing.map((c) => <span key={c.key} className="inline-flex items-center gap-1 text-xs font-semibold bg-amber-50 text-amber-700 px-2.5 py-1 rounded-full">{c.label}</span>)}</div>
            </div>
          )}
        </div>
        <button onClick={() => navigate(`/dashboard/properties/${id}`)} data-testid="wizard-see-guide" className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-blue-600 text-white font-semibold rounded-2xl py-3.5">Vedi la mia guida <ChevronRight className="w-4 h-4" /></button>
        <button onClick={() => navigate(`/dashboard/properties/${id}/edit`)} className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-slate-200 text-slate-700 font-semibold rounded-2xl py-3.5">Continua a modificare</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 max-w-md mx-auto flex flex-col">
      <header className="sticky top-0 z-20 bg-slate-50/90 backdrop-blur px-4 pt-4 pb-2">
        <div className="flex items-center justify-between">
          <button onClick={goBack} className="text-slate-500 p-1" data-testid="wizard-back"><ArrowLeft className="w-5 h-5" /></button>
          <span className="text-xs font-semibold text-slate-400">Creazione guida · {progress}%</span>
          <button onClick={exitToManual} className="text-xs font-semibold text-slate-400" data-testid="wizard-skip-all">Salta</button>
        </div>
        <div className="mt-2 h-1.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full rounded-full bg-blue-600 transition-all duration-500" style={{ width: `${progress}%` }} /></div>
      </header>

      <div key={s.id} className="flex-1 px-5 pt-6 pb-32 animate-fade-up">
        <div className="flex items-center gap-2 text-blue-600 mb-3"><Wand2 className="w-4 h-4" /><span className="text-xs font-bold uppercase tracking-wide">Creiamo insieme la tua guida</span></div>
        <h1 className="font-display font-extrabold text-2xl text-slate-900 leading-tight" data-testid="wizard-question">{s.q}</h1>
        {s.subtitle && <p className="text-slate-500 mt-1.5 text-sm">{s.subtitle}</p>}
        {s.hint && <p className="text-slate-400 mt-1.5 text-xs">{s.hint}</p>}

        <div className="mt-6 space-y-4">
          {s.type === "text" && (
            <input autoFocus type={s.inputType || "text"} value={answers[s.key] ?? ""} onChange={(e) => set(s.key, e.target.value)}
              data-testid={`wizard-input-${s.key}`} placeholder={s.placeholder}
              className="w-full rounded-2xl border border-slate-200 px-4 py-3.5 outline-none focus:border-blue-500 text-[16px]" />
          )}
          {s.type === "textarea" && (
            <textarea value={answers[s.key] ?? ""} onChange={(e) => set(s.key, e.target.value)} rows={5}
              data-testid={`wizard-input-${s.key}`} placeholder={s.placeholder}
              className="w-full rounded-2xl border border-slate-200 px-4 py-3.5 outline-none focus:border-blue-500 text-[15px] resize-none" />
          )}
          {s.type === "group" && s.fields.map((f) => <Field key={f.key} f={f} value={answers[f.key]} onChange={(v) => set(f.key, v)} />)}
          {s.type === "single" && (
            <>
              <div className="flex flex-wrap gap-2">{s.options.map((o) => <Chip key={o} active={answers[s.key] === o} onClick={() => set(s.key, o)} testid={`wizard-opt-${o}`}>{o}</Chip>)}</div>
              {s.conditional && s.conditional(answers) && <div className="mt-4 space-y-4 rounded-2xl bg-white border border-slate-100 p-4">{s.condFields.map((f) => <Field key={f.key} f={f} value={answers[f.key]} onChange={(v) => set(f.key, v)} />)}</div>}
            </>
          )}
          {s.type === "yesno" && (
            <>
              <div className="flex gap-2">
                <Chip active={answers[s.key] === true} onClick={() => set(s.key, true)} testid="wizard-yes">Sì</Chip>
                <Chip active={answers[s.key] === false} onClick={() => set(s.key, false)} testid="wizard-no">No</Chip>
              </div>
              {answers[s.key] === true && <div className="mt-4 space-y-4 rounded-2xl bg-white border border-slate-100 p-4">{s.yesFields.map((f) => <Field key={f.key} f={f} value={answers[f.key]} onChange={(v) => set(f.key, v)} />)}</div>}
            </>
          )}
          {s.type === "multi" && (
            <>
              <div className="flex flex-wrap gap-2">
                {s.options.map((o) => {
                  const lbl = (s.labels || []).find((x) => x.v === o)?.l || o;
                  const active = (answers[s.key] || []).includes(o);
                  return <Chip key={o} active={active} onClick={() => toggle(s.key, o)} testid={`wizard-opt-${o}`}>{lbl}</Chip>;
                })}
                {(answers[s.custom] || []).map?.((c, i) => <Chip key={`c${i}`} active onClick={() => setAnswers((a) => { const n = { ...a, [s.custom]: a[s.custom].filter((_, j) => j !== i) }; persist(n, step); return n; })}>{c} ✕</Chip>)}
              </div>
              {s.custom && (
                <div className="flex gap-2 mt-2">
                  <input value={customInput} onChange={(e) => setCustomInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCustom(s.custom)}
                    placeholder={s.customLabel} data-testid={`wizard-custom-${s.key}`}
                    className="flex-1 rounded-2xl border border-slate-200 px-4 py-2.5 outline-none focus:border-blue-500 text-sm" />
                  <button onClick={() => addCustom(s.custom)} className="rounded-2xl bg-slate-900 text-white px-4"><Plus className="w-4 h-4" /></button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 max-w-md mx-auto px-5 pb-5 pt-3 bg-gradient-to-t from-slate-50 via-slate-50 to-transparent">
        <button onClick={goNext} disabled={!canNext} data-testid="wizard-next"
          className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 text-white font-semibold rounded-2xl py-4 disabled:opacity-40 active:scale-[0.99] transition-transform">
          {step >= STEPS.length - 1 ? <>Genera la mia guida <Sparkles className="w-4 h-4" /></> : <>{s.optional && !(answers[s.key]) ? "Salta per ora" : "Continua"} <ArrowRight className="w-4 h-4" /></>}
        </button>
      </div>
    </div>
  );
}
